import { isToolUIPart, type DynamicToolUIPart, type ToolUIPart } from "ai";
import { AlertCircle, ArrowRightLeft, Square, Users } from "lucide-react";
import { Fragment, memo, useMemo, type FC, type ReactNode } from "react";

import { Message, MessageContent } from "@/components/ai-elements/message";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AttachmentList } from "@/features/agents/attachments/components/AttachmentChip";
import type { AttachmentFilePart } from "@/features/agents/attachments/types";
import { attachmentPartsOf } from "@/features/agents/attachments/utils";
import { ChatLink, ChatProse, segmentEntityRefs } from "@/features/agents/refs";
import {
  ASSISTANT_PERSONA,
  caseyName,
  type AgentChange,
  type ApprovalDecision,
  type FollowUpsData
} from "@/features/agents/types";
import {
  closeStalePauses,
  type StepsByTask
} from "@/features/agents/useAgentChat";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { AssistantByline } from "@/features/agents/components/AssistantByline";
import { TurnContextLine } from "@/features/agents/components/TurnContextLine";
import { cn } from "@/lib/utils";

import type {
  FailedInfo,
  SteerItem,
  StoppedInfo
} from "@/features/agents/api/agentApi";

import { useRuleCitations } from "@/features/companies/useRuleCitations";
import type { ConnectAppsOutput } from "@/features/connectors/types";
import { plainSkillRunText } from "@/features/firmKnowledge/skillRunText";
import { ResearchSourcesLine } from "../research/ResearchSourcesLine";
import type { AskUserAnswers } from "../../types";
import { initials, segmentMentions } from "../mentions";
import { isOfferDataPart } from "../offers/model";
import type { CaseAgentUIMessage } from "../useCaseAgentChat";
import { readWorkflowPrompt } from "../../workflows/catalog";
import { AssistantFooter } from "./AssistantFooter";
import { BuildCard } from "./BuildCard";
import { ChangeCard } from "./ChangeCard";
import { ExploredFold } from "./ExploredFold";
import { FollowUpsList } from "./FollowUpsList";
import {
  MessagePartsProvider,
  adoptedChangesOf,
  changesOf,
  isChangeLike
} from "./messageParts";
import { OfferCard } from "./OfferCard";
import { ModelUsedLabel, modelUsedOf } from "./ModelUsedLabel";
import { ReasoningRow } from "./ReasoningRow";
import { isHarnessTool } from "./toolTitles";
import {
  isFoldableRead,
  ToolCard,
  toolNameOf,
  type AnyToolPart
} from "./ToolCard";
import { rowStatusOf } from "./ToolRow";
import { TurnDetails } from "./TurnDetails";
import { splitDetails, toolPartsOf } from "./detailsSplit";
import { WorkflowPromptBody } from "./WorkflowPromptBody";

export { ModelUsedLabel, modelUsedCopy, modelUsedOf } from "./ModelUsedLabel";

type Part = CaseAgentUIMessage["parts"][number];

/**
 * A message's parts with runs of 2+ foldable read-only calls folded into one
 * `group` (Cursor's "Explored N files"). Everything else is a `single`.
 * Indices are the original part positions, for keys.
 */
type Segment =
  | { kind: "single"; index: number; part: Part }
  | { kind: "group"; index: number; parts: AnyToolPart[] };

const isDynamicTool = (p: { type: string }): p is DynamicToolUIPart =>
  p.type === "dynamic-tool";

const isTool = (p: Part): p is AnyToolPart =>
  isToolUIPart(p) || isDynamicTool(p);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

type SteerPart = { type: "data-steer"; id?: string; data: SteerItem };
type StoppedPart = { type: "data-stopped"; id?: string; data: StoppedInfo };
type FailedPart = { type: "data-failed"; id?: string; data: FailedInfo };
type ChangePart = { type: "data-change"; id?: string; data: AgentChange };
type FollowUpsPart = {
  type: "data-follow-ups";
  id?: string;
  data: FollowUpsData;
};

/**
 * Parts that say nothing on their own in the transcript: the live status line
 * reads `data-status` off the tail, `data-model` feeds the footer, and
 * `step-start` is the SDK's step boundary.
 */
const isSilent = (p: Part): boolean =>
  p.type === "data-status" ||
  p.type === "data-model" ||
  // Read by the approval card it describes, through `MessagePartsProvider`.
  p.type === "data-approval-review" ||
  p.type === "step-start" ||
  (isTool(p) && isHarnessTool(toolNameOf(p)));

/** A "send now" message the model took mid-turn (kept in the transcript). */
const isSteer = (p: Part): p is SteerPart => p.type === "data-steer";
/** The turn ended early: someone pressed Stop, or the client went away. */
const isStopped = (p: Part): p is StoppedPart => p.type === "data-stopped";
/** The turn ended in an error, or the server restarted under it. */
const isFailed = (p: Part): p is FailedPart =>
  p.type === "data-failed" && isRecord(p.data);
/** A write the agent made without asking, with Undo. */
const isChange = (p: Part): p is ChangePart =>
  p.type === "data-change" && isChangeLike(p.data);
/** What the reply left for later to keep the case consistent. */
const isFollowUps = (p: Part): p is FollowUpsPart =>
  p.type === "data-follow-ups" &&
  isRecord(p.data) &&
  Boolean(p.data.individual_id) &&
  Number(p.data.count) > 0;

/**
 * An assistant message cut at each "send now" message, in order: the agent's
 * parts up to the steer, the steer itself (rendered as the person's own
 * message row, like Cursor), the agent's parts after it. `start` is the
 * position of a chunk's first part in the original array, for keys.
 */
export type SteerChunk =
  | { kind: "assistant"; start: number; parts: Part[] }
  | { kind: "steer"; index: number; item: SteerItem };

export const splitAtSteers = (parts: readonly Part[]): SteerChunk[] => {
  const out: SteerChunk[] = [];
  let run: { start: number; parts: Part[] } | null = null;
  const flush = () => {
    if (run)
      out.push({ kind: "assistant", start: run.start, parts: run.parts });
    run = null;
  };
  parts.forEach((part, index) => {
    if (isSteer(part)) {
      flush();
      out.push({ kind: "steer", index, item: part.data });
      return;
    }
    if (run) run.parts.push(part);
    else run = { start: index, parts: [part] };
  });
  flush();
  return out;
};

const readPathOf = (part: Part): string | null => {
  if (!isTool(part)) return null;
  const { input } = part as AnyToolPart;
  if (!isRecord(input)) return null;
  const path = input.file_path ?? input.path ?? input.node_id;
  return typeof path === "string" && path ? path : null;
};

/**
 * The same file read again right after itself (the agent paging through one
 * SKILL.md): one row says it.
 */
const repeatsRead = (prev: Part | undefined, part: Part): boolean => {
  if (!prev || !isTool(part) || !isTool(prev)) return false;
  const path = readPathOf(part);
  return (
    path !== null &&
    path === readPathOf(prev) &&
    toolNameOf(part as AnyToolPart) === toolNameOf(prev as AnyToolPart) &&
    isFoldableRead(part as AnyToolPart)
  );
};

const idArgsOf = (part: AnyToolPart): Array<[string, unknown]> =>
  isRecord(part.input)
    ? Object.entries(part.input).filter(
        ([key, value]) => key.endsWith("_id") && value != null && value !== ""
      )
    : [];

/**
 * Failed calls the agent got through on a retry: a later call of the same
 * tool on the same ids that finished. Their red rows would make a run that
 * worked look broken. A failure that names no id, or that nothing later
 * fixed, still shows.
 */
export const supersededFailures = (parts: readonly Part[]): Set<string> => {
  const out = new Set<string>();
  const tools = parts.filter(isTool);
  tools.forEach((part, i) => {
    if (!part.toolCallId || rowStatusOf(part) !== "error") return;
    const ids = idArgsOf(part);
    if (!ids.length) return;
    const name = toolNameOf(part);
    const retried = tools.slice(i + 1).some((later) => {
      const input = isRecord(later.input) ? later.input : {};
      return (
        toolNameOf(later) === name &&
        rowStatusOf(later) === "done" &&
        ids.every(([key, value]) => input[key] === value)
      );
    });
    if (retried) out.add(part.toolCallId);
  });
  return out;
};

/**
 * Card layout: 2+ consecutive read-only calls fold into one "Explored N
 * files" line. Silent parts (status, model) are skipped and never break a
 * run; a status the agent emitted between two reads is still one exploration.
 * Calls in `hidden` (failures retried since) are skipped the same way.
 */
export const segmentParts = (
  parts: readonly Part[],
  hidden: ReadonlySet<string> = new Set()
): Segment[] => {
  const out: Segment[] = [];
  let run: { index: number; parts: AnyToolPart[] } | null = null;
  const flush = () => {
    if (!run) return;
    if (run.parts.length >= 2) out.push({ kind: "group", ...run });
    else out.push({ kind: "single", index: run.index, part: run.parts[0] });
    run = null;
  };
  parts.forEach((part, index) => {
    if (isSilent(part)) return;
    if (isTool(part) && hidden.has(part.toolCallId)) return;
    if (isTool(part) && isFoldableRead(part)) {
      if (run) {
        if (!repeatsRead(run.parts[run.parts.length - 1], part))
          run.parts.push(part);
      } else run = { index, parts: [part] };
      return;
    }
    flush();
    out.push({ kind: "single", index, part });
  });
  flush();
  return out;
};

/** The reply's prose, joined, for Copy. */
export const messageMarkdown = (
  message: Pick<CaseAgentUIMessage, "parts">
): string =>
  message.parts
    .filter((p): p is Extract<Part, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n\n")
    .trim();

/**
 * When a stored message was last written (epoch ms), if stamped: a reply's
 * last write is when its turn finished, not when it started.
 */
export const writtenAtOf = (message: CaseAgentUIMessage): number | null => {
  const stamps = message as {
    created_at_ns?: unknown;
    updated_at_ns?: unknown;
  };
  const ns =
    typeof stamps.updated_at_ns === "number"
      ? stamps.updated_at_ns
      : stamps.created_at_ns;
  if (typeof ns !== "number") return null;
  const ms = ns / 1_000_000;
  return Number.isNaN(ms) ? null : ms;
};

type Props = {
  message: CaseAgentUIMessage;
  isStreaming: boolean;
  onApproval: (decision: ApprovalDecision) => void;
  onAnswer?: (toolCallId: string, answers: AskUserAnswers) => void;
  onConnectResolved?: (toolCallId: string, output: ConnectAppsOutput) => void;
  /** The signed-in user's uid: own messages sit right, teammates' left. */
  meUid?: string | null;
  /**
   * The signed-in user's avatar. Own messages always render it, even when the
   * stored message predates avatar stamping or was reloaded without one.
   */
  meAvatarUrl?: string | null;
  /** Highlighted when the transcript is opened from a mention notification. */
  highlighted?: boolean;
  /**
   * Replaces the footer's own actions (copy, retry) under an assistant
   * message; the time and model still render.
   */
  actions?: ReactNode;
  /** Live subagent steps by `task` call id (the streaming message only). */
  steps?: StepsByTask;
  /** Retry this reply (the last one only); `model` re-runs it on the primary a fallback stood in for. */
  onRetry?: (options?: { model?: string }) => void;
  /**
   * An older message the reader has scrolled past: the browser skips its
   * layout and paint until it comes back into view.
   */
  settled?: boolean;
  /** A case chat: Export can also save a reply to Work Products. */
  canSaveToWorkProducts?: boolean;
  /**
   * Casey's byline over the reply (see `opensReplyTurn`): off for a reply that
   * continues the turn above it.
   */
  showByline?: boolean;
  /** Hide which model wrote the reply (the library chat). */
  hideModel?: boolean;
  /** Under a finished reply, above its footer (the library chat's sources). */
  replyAppendix?: (message: CaseAgentUIMessage) => ReactNode;
};

const NO_STEPS: StepsByTask = {};

/** User text with the composer's mention tokens rendered as chips. */
export const MentionText: FC<{ text: string; meUid?: string | null }> = ({
  text,
  meUid
}) => (
  <>
    {segmentMentions(text).map((seg, i) =>
      seg.type === "text" ? (
        <span key={i}>
          {segmentEntityRefs(seg.text).map((run, j) =>
            run.type === "text" ? (
              run.text
            ) : (
              <ChatLink key={j} href={run.href} title={run.title ?? undefined}>
                {run.label}
              </ChatLink>
            )
          )}
        </span>
      ) : (
        <span
          key={i}
          data-testid={`mention-${seg.kind}`}
          data-me={seg.kind === "user" && seg.id === meUid ? "true" : undefined}
          className={cn(
            "font-medium",
            seg.kind === "artifact" ? "text-fg-2" : "text-agents-accent",
            seg.kind === "user" && seg.id === meUid && "rounded-xs bg-info-soft"
          )}
        >
          @{seg.label}
        </span>
      )
    )}
  </>
);

/**
 * A person's message: a right-aligned tinted bubble with its time at the end.
 * A teammate's or another agent's message also names its author there. Used
 * for user messages and for "send now" messages, which Cursor shows as
 * ordinary user messages at the point in the agent's reply where they were
 * delivered.
 */
const UserRow: FC<{
  id?: string;
  testId: string;
  mine: boolean;
  name: string;
  avatarUrl: string | null;
  time: string | null;
  text: string;
  /** Shown in the card instead of `text`. */
  body?: ReactNode;
  /** The message's attached files, under the card. */
  files?: AttachmentFilePart[];
  meUid?: string | null;
  /** The small badge after the name (audience / handoff / agent mark). */
  badge?: ReactNode;
  /** The avatar's fallback: initials by default. */
  avatarFallback?: ReactNode;
  audience?: "team" | "agent";
  authorKind?: "user" | "agent";
  highlighted?: boolean;
  cardClassName?: string;
  /** Under the card: the record the turn was sent about. */
  footer?: ReactNode;
  className?: string;
}> = memo(
  ({
    id,
    testId,
    mine,
    name,
    avatarUrl,
    time,
    text,
    body,
    files,
    meUid,
    badge,
    avatarFallback,
    audience,
    authorKind,
    highlighted,
    cardClassName,
    footer,
    className
  }) => {
    const meta = (
      <span
        data-slot="user-message-meta"
        className="flex shrink-0 items-center gap-1.5 text-sm text-fg-4"
      >
        {badge}
        {mine ? null : (
          <span className="flex items-center gap-1 text-fg-3">
            <Avatar size="2xs">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
              <AvatarFallback
                className={cn(authorKind === "agent" && "bg-transparent")}
              >
                {avatarFallback ?? initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="max-w-28 truncate">{name}</span>
          </span>
        )}
        {time ? <span className="tabular-nums">{time}</span> : null}
      </span>
    );
    const hasCard = Boolean(text || body) || !files?.length;
    return (
      <div
        id={id}
        data-testid={testId}
        data-mine={mine ? "true" : "false"}
        data-audience={audience}
        data-author-kind={authorKind}
        className={cn(
          "flex w-full flex-col items-end gap-1 rounded-xl transition-colors",
          highlighted && "bg-warn-soft ring-1 ring-warn-line",
          className
        )}
      >
        {hasCard ? (
          <Message from="user">
            <MessageContent
              data-slot="user-message-card"
              className={cn("flex-row items-start gap-2", cardClassName)}
            >
              {body ? (
                <div className="min-w-0 flex-1">{body}</div>
              ) : (
                <span className="min-w-0 flex-1 whitespace-pre-wrap">
                  <MentionText text={text} meUid={meUid} />
                </span>
              )}
              <span className="flex pt-0.75">{meta}</span>
            </MessageContent>
          </Message>
        ) : (
          <span className="flex justify-end px-1">{meta}</span>
        )}
        {files?.length ? (
          <AttachmentList parts={files} className="mt-1 justify-end" />
        ) : null}
        {footer}
      </div>
    );
  }
);
UserRow.displayName = "UserRow";

const clockOf = (iso: string): string | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/**
 * A "send now" message where it landed in the agent's turn: the person's own
 * message row (Cursor does not fold it into the previous message).
 */
export const SteerMessage: FC<{
  item: SteerItem;
  meUid?: string | null;
  meAvatarUrl?: string | null;
}> = ({ item, meUid, meAvatarUrl }) => {
  const mine = !meUid || item.uid === meUid;
  if (item.build) {
    // A Build that reached a running turn (older threads): the plan, as the
    // Build card shows it, never the prompt that went to the agent.
    return (
      <BuildCard
        message={{
          id: item.message_id || item.id,
          role: "user",
          metadata: { build: item.build }
        }}
      />
    );
  }
  return (
    <UserRow
      testId="steer-message"
      mine={mine}
      name={item.name || "Someone"}
      avatarUrl={mine ? meAvatarUrl ?? null : null}
      time={clockOf(item.created_at)}
      text={item.text}
      meUid={meUid}
      audience="agent"
      authorKind="user"
    />
  );
};

/** What the transcript says where a turn ended early. */
export const stoppedCopy = (
  info: StoppedInfo,
  meUid?: string | null
): string => {
  if (info.by === "disconnected") return "Stopped: the connection was lost";
  const who = !info.uid || !meUid || info.uid === meUid ? "You" : info.name;
  return `${who || "Someone"} stopped the agent`;
};

/** Where a turn ended early: a hairline with a small label, Cursor's. */
export const StoppedMarker: FC<{
  info: StoppedInfo;
  meUid?: string | null;
  className?: string;
}> = ({ info, meUid, className }) => (
  <div
    className={cn(
      "flex items-center gap-2 py-0.5 text-xs text-fg-3",
      className
    )}
    data-testid="stopped-marker"
    data-stopped-by={info.by}
  >
    <span className="h-px flex-1 bg-line-2" aria-hidden />
    <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
      <Square className="size-2 shrink-0 fill-current" />
      {stoppedCopy(info, meUid)}
    </span>
    <span className="h-px flex-1 bg-line-2" aria-hidden />
  </div>
);

/** Where a turn ended in an error, in the stopped marker's place. */
export const FailedMarker: FC<{ info: FailedInfo; className?: string }> = ({
  info,
  className
}) => (
  <div
    className={cn(
      "flex items-center gap-2 py-0.5 text-xs text-destructive",
      className
    )}
    data-testid="failed-marker"
    role="status"
  >
    <span className="h-px flex-1 bg-destructive/30" aria-hidden />
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <AlertCircle className="size-3 shrink-0" />
      {info.message || "Something went wrong. Please try again."}
    </span>
    <span className="h-px flex-1 bg-destructive/30" aria-hidden />
  </div>
);

const timeOf = (message: CaseAgentUIMessage): string | null => {
  const ns = (message as { created_at_ns?: unknown }).created_at_ns;
  if (typeof ns !== "number") return null;
  const d = new Date(ns / 1_000_000);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/** The message with pause cards a lost run left open shown as closed. */
const useShownMessage = (message: CaseAgentUIMessage): CaseAgentUIMessage =>
  useMemo(() => {
    const parts = closeStalePauses(message.parts);
    return parts === message.parts
      ? message
      : { ...message, parts: parts as Part[] };
  }, [message]);

/**
 * The agent's reply: no bubble, on the surface, under Casey's byline. Prose,
 * thin tool rows, "Explored N files" folds, the cards that need a person,
 * then a quiet footer.
 */
const AssistantMessage: FC<Props> = ({
  message: received,
  isStreaming,
  onApproval,
  onAnswer,
  onConnectResolved,
  meUid,
  meAvatarUrl,
  highlighted,
  actions,
  steps = NO_STEPS,
  onRetry,
  settled,
  canSaveToWorkProducts,
  showByline = true,
  hideModel,
  replyAppendix
}) => {
  const message = useShownMessage(received);
  // "Send now" messages cut the reply: the agent's parts before, the
  // person's row, the agent's parts after, all in this one message.
  const chunks = useMemo(() => splitAtSteers(message.parts), [message.parts]);
  // Across the whole message: a retry can land after a steer.
  const retried = useMemo(
    () => supersededFailures(message.parts),
    [message.parts]
  );
  const segmented = useMemo(
    () =>
      chunks.map((chunk) =>
        chunk.kind === "steer"
          ? chunk
          : {
              ...chunk,
              segments: segmentParts(chunk.parts, retried).map((seg) => ({
                ...seg,
                index: seg.index + chunk.start
              }))
            }
      ),
    [chunks, retried]
  );
  // The last part that shows: the one still streaming, if any.
  const lastIndex = useMemo(() => {
    for (let i = message.parts.length - 1; i >= 0; i -= 1) {
      if (!isSilent(message.parts[i])) return i;
    }
    return -1;
  }, [message.parts]);
  const modelUsed = modelUsedOf(message);
  const markdown = useMemo(() => messageMarkdown(message), [message]);
  // Changes whose tool row shows them (with Undo) need no card of their own.
  const adopted = useMemo(
    () => adoptedChangesOf(message.parts),
    [message.parts]
  );
  const allTools = useMemo(() => toolPartsOf(message.parts), [message.parts]);
  const { changesByCall } = useMemo(
    () => changesOf(message.parts),
    [message.parts]
  );
  const cite = useRuleCitations();
  const writtenAt = writtenAtOf(message);
  const lastChunk = segmented.length - 1;
  return (
    <MessagePartsProvider parts={message.parts}>
      <Message
        from="assistant"
        id={`msg-${message.id}`}
        data-testid="assistant-message"
        className={cn(
          "group/message rounded-md transition-colors",
          settled && "[content-visibility:auto]",
          highlighted && "bg-warn-soft ring-1 ring-warn-line"
        )}
      >
        <MessageContent className="w-full gap-1.5 bg-transparent p-0 text-left text-md group-[.is-assistant]:w-full">
          {showByline ? <AssistantByline /> : null}
          {segmented.map((chunk, c) => {
            if (chunk.kind === "steer") {
              return (
                <SteerMessage
                  key={`${message.id}-${chunk.index}`}
                  item={chunk.item}
                  meUid={meUid}
                  meAvatarUrl={meAvatarUrl}
                />
              );
            }
            const lastSegment = chunk.segments.length - 1;
            const renderSegment = (
              seg: (typeof chunk.segments)[number],
              s: number
            ) => {
              const i = seg.index;
              const key = `${message.id}-${i}`;
              const isTail =
                isStreaming && c === lastChunk && s === lastSegment;
              if (seg.kind === "group") {
                return (
                  <ExploredFold
                    key={key}
                    parts={seg.parts}
                    // The fold still being worked in stays open; done ones fold.
                    live={isTail}
                    onApproval={onApproval}
                    onAnswer={onAnswer}
                    onConnectResolved={onConnectResolved}
                  />
                );
              }
              const { part } = seg;
              if (part.type === "text") {
                if (!part.text.trim()) return null;
                return (
                  <ChatProse
                    key={key}
                    streaming={isStreaming && i === lastIndex}
                    className="my-1"
                  >
                    {cite(part.text)}
                  </ChatProse>
                );
              }
              if (part.type === "reasoning") {
                if (!part.text.trim()) return null;
                return (
                  <ReasoningRow
                    key={key}
                    text={part.text}
                    isStreaming={isStreaming && i === lastIndex}
                  />
                );
              }
              if (isTool(part)) {
                return (
                  <ToolCard
                    key={key}
                    part={part}
                    onApproval={onApproval}
                    onAnswer={onAnswer}
                    onConnectResolved={onConnectResolved}
                    steps={steps[part.toolCallId]}
                  />
                );
              }
              if (isStopped(part)) {
                return (
                  <StoppedMarker key={key} info={part.data} meUid={meUid} />
                );
              }
              if (isFailed(part)) {
                return <FailedMarker key={key} info={part.data} />;
              }
              if (isChange(part)) {
                if (adopted.has(part.data.id)) return null;
                return <ChangeCard key={key} change={part.data} />;
              }
              if (isFollowUps(part)) {
                return (
                  <FollowUpsList
                    key={key}
                    data={part.data}
                    messageId={message.id}
                  />
                );
              }
              if (isOfferDataPart(part)) {
                return <OfferCard key={key} data={part.data} />;
              }
              return null;
            };
            // A finished reply leads with its outcome: the process folds
            // into "Details" (see detailsSplit.ts for what folds).
            const split = isStreaming
              ? null
              : splitDetails(chunk.segments, allTools, changesByCall);
            if (!split || split.folded.length === 0)
              return chunk.segments.map(renderSegment);
            const at = (seg: (typeof chunk.segments)[number]) =>
              chunk.segments.indexOf(seg);
            return (
              <Fragment key={`${message.id}-${chunk.start}-details`}>
                <TurnDetails steps={split.steps} changes={split.changes}>
                  {split.folded.map((seg) => renderSegment(seg, at(seg)))}
                </TurnDetails>
                {split.shown.map((seg) => renderSegment(seg, at(seg)))}
              </Fragment>
            );
          })}
          {!isStreaming && replyAppendix ? replyAppendix(message) : null}
          {!isStreaming ? (
            <AssistantFooter
              markdown={actions ? "" : markdown}
              writtenAt={writtenAt}
              model={hideModel ? null : modelUsed}
              onRetry={actions ? undefined : onRetry}
              canSaveToWorkProducts={canSaveToWorkProducts}
              className="mt-0.5"
            >
              {actions}
            </AssistantFooter>
          ) : null}
        </MessageContent>
      </Message>
    </MessagePartsProvider>
  );
};

export const ChatMessage: FC<Props> = memo(
  ({
    message,
    isStreaming,
    onApproval,
    onAnswer,
    onConnectResolved,
    meUid,
    meAvatarUrl,
    highlighted,
    actions,
    steps = NO_STEPS,
    onRetry,
    settled,
    canSaveToWorkProducts,
    showByline,
    hideModel,
    replyAppendix
  }) => {
    if (message.role === "user") {
      // An automation run's first message stored with the old wording
      // ("Run the skill **X** …") reads in the plain one.
      const text = plainSkillRunText(
        message.parts
          .filter((p): p is { type: "text"; text: string } => p.type === "text")
          .map((p) => p.text)
          .join("\n")
      );
      const files = attachmentPartsOf(message.parts);
      const author = message.metadata?.author ?? null;
      if (message.metadata?.build) {
        // Build was pressed on a plan: Cursor shows the plan and its todo
        // progress here, never the prompt that went to the agent.
        return <BuildCard message={message} highlighted={highlighted} />;
      }
      if (author?.kind === "system") {
        // Where a Continue in Case thread picked up from, for both readers.
        if (message.metadata?.continued_from)
          return (
            <div
              id={`msg-${message.id}`}
              data-testid="continued-note"
              className="rounded-lg bg-fill-1 px-3 py-2 text-sm whitespace-pre-line text-fg-2"
            >
              {text}
            </div>
          );
        // Background work the agent started finished and the backend woke the
        // agent. The notice is for the model; the attorney sees the run's card
        // turn done and the agent's reply.
        return null;
      }
      const isTeam = message.metadata?.audience === "team";
      // Another scope's agent posted this (a handoff brief): never "mine".
      const fromAgent = author?.kind === "agent";
      // A message with no author stamp is this client's own (pre-reload).
      const mine = !fromAgent && (!author || !meUid || author.uid === meUid);
      const name = fromAgent
        ? caseyName(author?.name ?? ASSISTANT_PERSONA)
        : author?.name ?? "You";
      // My own avatar comes from the live profile so it renders even on
      // messages stored before avatar stamping (or reloaded without one).
      const avatarUrl = mine
        ? meAvatarUrl ?? author?.avatar_url ?? null
        : author?.avatar_url ?? null;
      const time = timeOf(message);
      // A Case Prep run reads as its workflow line, from the text alone so
      // reloaded history and older prompt versions read the same way.
      const workflow = fromAgent ? null : readWorkflowPrompt(text);
      return (
        <UserRow
          id={`msg-${message.id}`}
          testId="user-message"
          mine={mine}
          name={name}
          avatarUrl={avatarUrl}
          time={time}
          text={text}
          body={
            workflow ? (
              <WorkflowPromptBody match={workflow} text={text} />
            ) : undefined
          }
          files={files}
          meUid={meUid}
          audience={isTeam ? "team" : "agent"}
          authorKind={fromAgent ? "agent" : "user"}
          highlighted={highlighted}
          className="not-first:mt-2"
          avatarFallback={
            fromAgent ? (
              <AgentMoodAvatar
                animated
                className="size-full rounded-[inherit]"
                agent={{
                  key:
                    author?.scope_kind && author.scope_id
                      ? `${author.scope_kind}:${author.scope_id}`
                      : author?.uid
                }}
              />
            ) : undefined
          }
          badge={
            fromAgent ? (
              <span
                className="inline-flex items-center gap-0.5 text-primary"
                title="Handed over by another agent"
                data-testid="handoff-badge"
              >
                <ArrowRightLeft className="size-3" /> Handoff
              </span>
            ) : isTeam ? (
              <span
                className="inline-flex items-center gap-0.5"
                title="Team message; the agent was not asked"
                data-testid="team-badge"
              >
                <Users className="size-3" /> Team
              </span>
            ) : undefined
          }
          cardClassName={
            isTeam
              ? "group-[.is-user]:bg-fill-2 dark:group-[.is-user]:bg-fill-2"
              : undefined
          }
          footer={
            message.metadata?.context ||
            message.metadata?.research_sources?.length ? (
              <>
                {message.metadata.research_sources?.length ? (
                  <ResearchSourcesLine
                    sources={message.metadata.research_sources}
                  />
                ) : null}
                {message.metadata.context ? (
                  <TurnContextLine context={message.metadata.context} />
                ) : null}
              </>
            ) : undefined
          }
        />
      );
    }

    return (
      <ErrorBoundary
        scope="chat-message"
        resetKey={message.parts.length}
        fallback={
          <div
            role="alert"
            id={`msg-${message.id}`}
            data-testid="message-failed"
            className="rounded-lg bg-fill-1 px-3 py-2 text-sm text-fg-3"
          >
            This step could not be shown.
          </div>
        }
      >
        <AssistantMessage
          message={message}
          isStreaming={isStreaming}
          onApproval={onApproval}
          onAnswer={onAnswer}
          onConnectResolved={onConnectResolved}
          meUid={meUid}
          meAvatarUrl={meAvatarUrl}
          highlighted={highlighted}
          actions={actions}
          steps={steps}
          onRetry={onRetry}
          settled={settled}
          canSaveToWorkProducts={canSaveToWorkProducts}
          showByline={showByline}
          hideModel={hideModel}
          replyAppendix={replyAppendix}
        />
      </ErrorBoundary>
    );
  }
);
ChatMessage.displayName = "ChatMessage";
